import { Directive, forwardRef, HostBinding, inject } from '@angular/core';

import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import { A11yRole } from '../interfaces/a11y.interfaces';
import { A11yKeyboardService } from '../services/a11y-keyboard.service';
import { A11yRovingTabindexService } from '../services/a11y-roving-tabindex.service';

import { A11yLabelDirective } from './a11y-label.directive';
import { A11yRadioDirective } from './a11y-radio.directive';

@Directive({
  selector: '[khA11yRadioGroup]',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yRadioGroupDirective) }]
})
export class A11yRadioGroupDirective extends A11yLabelDirective implements A11yRole {
  private readonly keyboardService = inject(A11yKeyboardService);
  private readonly rovingTabindexService = inject(A11yRovingTabindexService);
  private readonly radios: A11yRadioDirective[] = [];

  @HostBinding('attr.role') public readonly role = 'radiogroup';

  public registerRadio(radio: A11yRadioDirective): void {
    if (!this.radios.includes(radio)) {
      this.radios.push(radio);
    }
  }

  public unregisterRadio(radio: A11yRadioDirective): void {
    const index = this.radios.indexOf(radio);

    if (index >= 0) {
      this.radios.splice(index, 1);
    }
  }

  public handleRadioKeydown(radio: A11yRadioDirective, event: KeyboardEvent): boolean {
    return this.keyboardService.handleArrowNavigation(event, (direction) => this.moveRadio(radio, direction));
  }

  private moveRadio(currentRadio: A11yRadioDirective, direction: 1 | -1): void {
    const enabledRadios = this.radios.filter((radio) => !radio.isDisabled);
    const currentIndex = enabledRadios.indexOf(currentRadio);

    if (currentIndex < 0) {
      return;
    }

    const radioElements = enabledRadios.map((radio) => radio.element);
    const nextIndex = direction > 0
      ? this.rovingTabindexService.moveNext(radioElements, currentIndex)
      : this.rovingTabindexService.movePrevious(radioElements, currentIndex);

    this.rovingTabindexService.update(radioElements, nextIndex);
    enabledRadios[nextIndex].activate();
  }
}
